// Centralized status computation logic for estimates
// This ensures consistency between display and filtering logic

type EstimateStatus = 'draft' | 'sent' | 'approved' | 'rejected' | 'expired' | 'cancelled';

/**
 * Computes the effective status of an estimate
 * This logic must match exactly between display and filtering
 */
export const getEffectiveEstimateStatus = (
  status: EstimateStatus, 
  expiresAt?: string | null
): EstimateStatus => {
  // Cancelled, approved, and rejected are permanent states - don't override with expired
  if (status === 'cancelled' || status === 'approved' || status === 'rejected') {
    return status;
  }
  
  // Check if estimate is expired
  const isExpired = expiresAt && new Date(expiresAt) < new Date();
  
  if (isExpired) {
    return 'expired';
  }
  
  return status;
};

/**
 * Gets display badge variant for status
 */
export const getStatusBadgeVariant = (effectiveStatus: EstimateStatus) => {
  switch (effectiveStatus) {
    case 'draft':
      return 'outline';
    case 'sent':
      return 'secondary';
    case 'approved':
      return 'default';
    case 'rejected':
    case 'expired':
      return 'destructive';
    case 'cancelled':
      return 'secondary';
    default:
      return 'outline';
  }
};

/**
 * Gets display label for status
 */
export const getStatusLabel = (effectiveStatus: EstimateStatus): string => {
  switch (effectiveStatus) {
    case 'draft':
      return 'Draft';
    case 'sent':
      return 'Sent';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'expired':
      return 'Expired';
    case 'cancelled':
      return 'Cancelled';
    default:
      return effectiveStatus;
  }
};